import React from 'react';
import type { OutcomeTotals } from '@root/lib';
import { useImageUrl } from '@shared/components/ImageContext';
import { formatMoney } from '@shared/lib';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import styles from './RunOutcomeDialog.module.scss';

export interface RunOutcomeDialogProps {
  title: string;
  // Image key, not a path.
  emblem: string;
  headline: string;
  won: boolean;
  totals: OutcomeTotals;
  note: string;
  symbol: string;
  onNewRound: () => void;
  onViewDetails: () => void;
  onMenu: () => void;
}

// Every ending reads the same: won, eliminated, given up, expired.
const RunOutcomeDialog: React.FC<RunOutcomeDialogProps> = ({
  title,
  emblem,
  headline,
  won,
  totals,
  note,
  symbol,
  onNewRound,
  onViewDetails,
  onMenu,
}) => {
  const { t } = useTranslation();
  const { img, imgProps } = useImageUrl();
  const money = (value: number): string => formatMoney(symbol, value);

  const rows: [string, string][] = [
    [t('Total spent'), money(totals.spent)],
    [t('Multiplier rewards'), money(totals.multipliers)],
    [t('Finish reward'), money(totals.finish)],
    [t('Total win'), money(totals.won)],
    [
      t('Net profit / loss'),
      // Figma uses a real minus sign, not a hyphen.
      totals.net < 0 ? `−${money(-totals.net)}` : `+${money(totals.net)}`,
    ],
  ];

  const skin = (primary: boolean) =>
    ({
      '--button-skin': `url(${img(
        primary ? 'ButtonSkin_Primary' : 'ButtonSkin_Secondary'
      )})`,
    }) as React.CSSProperties;

  return (
    <div className={styles.scrim}>
      <div
        className={styles.dialog}
        style={
          { '--panel-skin': `url(${img('PopupFrame')})` } as React.CSSProperties
        }
      >
        <div className={styles.head}>
          <h2 className={styles.title}>{title}</h2>
        </div>

        <div className={styles.emblem}>
          <img {...imgProps(emblem)} alt="" />
        </div>
        <p className={cn(styles.headline, { [styles.headline_loss]: !won })}>
          {headline}
        </p>

        {rows.map(([label, value]) => (
          <div className={styles.row} key={label}>
            <span className={styles.rowLabel}>{label}</span>
            <span className={styles.rowValue}>{value}</span>
          </div>
        ))}

        <p className={styles.note}>{note}</p>

        <button
          type="button"
          className={cn(styles.skin, styles.skin_primary)}
          style={skin(true)}
          onClick={onNewRound}
        >
          {t('NEW ROUND')}
        </button>
        <button
          type="button"
          className={cn(styles.skin, styles.skin_secondary)}
          style={skin(false)}
          onClick={onViewDetails}
        >
          {t('VIEW ROUND DETAILS')}
        </button>
        <button
          type="button"
          className={cn(styles.skin, styles.skin_secondary, styles.skin_short)}
          style={skin(false)}
          onClick={onMenu}
        >
          {t('MENU')}
        </button>
      </div>
    </div>
  );
};

export default RunOutcomeDialog;
